import { Organization } from '@klear/organization'

export type InternationalFinancialAccountIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type InternationalFinancialAccountMeta = {
  entityId?: number
  entityNumber?: string
  defaultPurposeId?: number
  defaultFundingSourceId?: number
}

export type InternationalFinancialAccountJson = {
  identity: InternationalFinancialAccountIdentity
  organizationId: number
  financialAccountId: string
  meta: InternationalFinancialAccountMeta
  organizationName?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export function internationalFinancialAccountToJson(
  account: InternationalFinancialAccount
): InternationalFinancialAccountJson {
  return {
    identity: account.identity,
    organizationId: account.organizationId,
    financialAccountId: account.financialAccountId,
    meta: account.meta,
    organizationName: account.organization.name || undefined,
    createdAt: account.createdAt,
    updatedAt: account.updatedAt,
    deletedAt: account.deletedAt,
    createdByUserId: account.createdByUserId,
    createdByUserEmail: account.createdByUserEmail,
    updatedByUserId: account.updatedByUserId,
    updatedByUserEmail: account.updatedByUserEmail,
    deletedByUserId: account.deletedByUserId,
    deletedByUserEmail: account.deletedByUserEmail
  }
}

export class InternationalFinancialAccount {
  static readonly __klearClass = 'InternationalFinancialAccount'

  readonly identity: InternationalFinancialAccountIdentity
  organizationId: number
  financialAccountId: string
  meta: InternationalFinancialAccountMeta
  organization: Organization
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  constructor(props: InternationalFinancialAccountJson) {
    this.identity = props.identity
    this.organizationId = props.organizationId
    this.financialAccountId = props.financialAccountId
    this.meta = props.meta ?? {}
    this.organization = new Organization({ name: props.organizationName ?? '' })
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get entityId() {
    return this.meta.entityId
  }

  get entityNumber() {
    return this.meta.entityNumber
  }

  get defaultPurposeId() {
    return this.meta.defaultPurposeId
  }

  get defaultFundingSourceId() {
    return this.meta.defaultFundingSourceId
  }

  get monexCustomerNumber() {
    return this.financialAccountId
  }

  readonly isKlearAccount = false

  readonly name = 'International Financial Account'

  get last4() {
    return this.financialAccountId.slice(-4)
  }

  get nameDetailed() {
    return `${this.name} - ${this.last4}`
  }

  get nameAndLast4() {
    return `${this.name} - ${this.last4}`
  }

  get accountHolderName() {
    return this.organization?.name ?? ''
  }

  get accountNumber() {
    return this.financialAccountId
  }

  readonly routingNumber = ''

  readonly bankName = ''

  toJSON() {
    return internationalFinancialAccountToJson(this)
  }
}
